import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';

const IconBold = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
        <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
    </svg>
);
const IconItalic = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="19" y1="4" x2="10" y2="4" />
        <line x1="14" y1="20" x2="5" y2="20" />
        <line x1="15" y1="4" x2="9" y2="20" />
    </svg>
);
const IconUnderline = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
        <line x1="4" y1="21" x2="20" y2="21" />
    </svg>
);
const IconAlignLeft = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="17" y1="10" x2="3" y2="10" />
        <line x1="21" y1="6" x2="3" y2="6" />
        <line x1="21" y1="14" x2="3" y2="14" />
        <line x1="17" y1="18" x2="3" y2="18" />
    </svg>
);
const IconAlignCenter = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="18" y1="10" x2="6" y2="10" />
        <line x1="21" y1="6" x2="3" y2="6" />
        <line x1="21" y1="14" x2="3" y2="14" />
        <line x1="18" y1="18" x2="6" y2="18" />
    </svg>
);
const IconAlignRight = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="21" y1="10" x2="7" y2="10" />
        <line x1="21" y1="6" x2="3" y2="6" />
        <line x1="21" y1="14" x2="3" y2="14" />
        <line x1="21" y1="18" x2="7" y2="18" />
    </svg>
);
const IconAlignJustify = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="21" y1="10" x2="3" y2="10" />
        <line x1="21" y1="6" x2="3" y2="6" />
        <line x1="21" y1="14" x2="3" y2="14" />
        <line x1="21" y1="18" x2="3" y2="18" />
    </svg>
);
const IconBulletList = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <circle cx="4" cy="6" r="1" fill="currentColor" />
        <circle cx="4" cy="12" r="1" fill="currentColor" />
        <circle cx="4" cy="18" r="1" fill="currentColor" />
    </svg>
);
const IconOrderedList = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="10" y1="6" x2="21" y2="6" />
        <line x1="10" y1="12" x2="21" y2="12" />
        <line x1="10" y1="18" x2="21" y2="18" />
        <text x="2" y="8" fontSize="8" fill="currentColor" stroke="none" fontWeight="600">
            1
        </text>
        <text x="2" y="14" fontSize="8" fill="currentColor" stroke="none" fontWeight="600">
            2
        </text>
        <text x="2" y="20" fontSize="8" fill="currentColor" stroke="none" fontWeight="600">
            3
        </text>
    </svg>
);

// --- Toolbar Button ---
const ToolbarButton = ({ icon, title, isActive, onClick, disabled }) => (
    <button
        type="button"
        className={`tiptap-toolbar-btn ${isActive ? 'is-active' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        disabled={disabled}
        title={title}
    >
        {icon}
    </button>
);

const ToolbarDivider = () => <div className="tiptap-toolbar-divider" />;

/**
 * SimpleTextEditor - A lightweight Tiptap editor that replaces ReactQuill.
 * Supports: bold, italic, underline, text alignment, text color, highlight, ordered list, bullet list.
 * Props: value (HTML string), onChange (HTML string), placeholder, readOnly
 */
export const SimpleTextEditor = ({ value, onChange, placeholder = 'Write Here...', readOnly = false, error, border }) => {
    const isInternalUpdate = useRef(false);
    const lastHtmlRef = useRef(value || '');

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                codeBlock: false,
                blockquote: false,
                horizontalRule: false,
                hardBreak: true,
            }),
            Underline,
            TextAlign.configure({
                types: ['paragraph'],
            }),
            TextStyle,
            Color,
            Highlight.configure({
                multicolor: true,
            }),
            Placeholder.configure({
                placeholder,
            }),
        ],
        content: value || '',
        editable: !readOnly,
        onUpdate: ({ editor }) => {
            isInternalUpdate.current = true;
            const html = editor.getHTML();
            const normalized = html === '<p></p>' ? '' : html;
            lastHtmlRef.current = normalized;
            onChange?.(normalized);
        },
    });

    useEffect(() => {
        if (editor) editor.setEditable(!readOnly);
    }, [readOnly, editor]);

    useEffect(() => {
        if (!editor) return;
        if (isInternalUpdate.current) {
            isInternalUpdate.current = false;
            return;
        }
        const normalizedValue = value || '';
        if (normalizedValue !== lastHtmlRef.current) {
            lastHtmlRef.current = normalizedValue;
            editor.commands.setContent(normalizedValue, false);
        }
    }, [value, editor]);

    if (!editor) return null;

    const errorMessage = error?.message || error;

    return (
        <div className="client-note-editor">
            <div
                className="note-text-editor-wrapper"
                style={{
                    border: border ? border : errorMessage ? '1px solid #FE7070' : '1px solid #EBF2FD',
                    borderRadius: 4,
                }}
            >
                <div className="simple-text-editor">
                    {!readOnly && (
                        <div className="simple-text-editor-toolbar">
                            <ToolbarButton
                                icon={<IconBold />}
                                title="Bold"
                                isActive={editor.isActive('bold')}
                                onClick={() => editor.chain().focus().toggleBold().run()}
                            />
                            <ToolbarButton
                                icon={<IconItalic />}
                                title="Italic"
                                isActive={editor.isActive('italic')}
                                onClick={() => editor.chain().focus().toggleItalic().run()}
                            />
                            <ToolbarButton
                                icon={<IconUnderline />}
                                title="Underline"
                                isActive={editor.isActive('underline')}
                                onClick={() => editor.chain().focus().toggleUnderline().run()}
                            />

                            <ToolbarDivider />

                            <ToolbarButton
                                icon={<IconAlignLeft />}
                                title="Align Left"
                                isActive={editor.isActive({ textAlign: 'left' })}
                                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                            />
                            <ToolbarButton
                                icon={<IconAlignCenter />}
                                title="Align Center"
                                isActive={editor.isActive({ textAlign: 'center' })}
                                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                            />
                            <ToolbarButton
                                icon={<IconAlignRight />}
                                title="Align Right"
                                isActive={editor.isActive({ textAlign: 'right' })}
                                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                            />
                            <ToolbarButton
                                icon={<IconAlignJustify />}
                                title="Justify"
                                isActive={editor.isActive({ textAlign: 'justify' })}
                                onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                            />

                            <ToolbarDivider />

                            <ToolbarButton
                                icon={<IconBulletList />}
                                title="Bullet List"
                                isActive={editor.isActive('bulletList')}
                                onClick={() => editor.chain().focus().toggleBulletList().run()}
                            />
                            <ToolbarButton
                                icon={<IconOrderedList />}
                                title="Ordered List"
                                isActive={editor.isActive('orderedList')}
                                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                            />
                        </div>
                    )}
                    <EditorContent editor={editor} />
                </div>
            </div>
            <p className="error-message">{errorMessage || ''}</p>
        </div>
    );
};
